import { useHeader } from '@/contexts';
import {
  RelatoriosContext,
  type FiltrosHeaderSnapshot,
  type FiltrosRelatorio,
  type Relatorio,
  type RelatoriosContextValue,
} from '@/contexts/RelatoriosContext';
import { competenciaPadrao } from '@/contexts/header-store';
import { useListarRelatorios } from '@/services/Relatorios';
import { useEffect, useCallback, useMemo, useState, type PropsWithChildren } from 'react';

const FILTROS_RELATORIO_INICIAIS: FiltrosRelatorio = {
  tipoRelatorio: 'ICMS',
  operacao: 'Todos',
  documento: 'Todos',
  busca: '',
};

/** O mesmo padrão do cabeçalho depois do "Limpar filtros": o mês corrente (SPEC-017, decisão 13). */
function criarFiltrosHeaderPadrao(): FiltrosHeaderSnapshot {
  return { empresaId: null, empresaCnpjs: null, ...competenciaPadrao() };
}

function extrairCnpjsSelecionados(
  filtrosHeader: Pick<FiltrosHeaderSnapshot, 'empresaId' | 'empresaCnpjs'>
) {
  const listaBruta = filtrosHeader.empresaCnpjs
    ? filtrosHeader.empresaCnpjs.split(',')
    : filtrosHeader.empresaId
      ? [filtrosHeader.empresaId]
      : [];

  return [...new Set(listaBruta.map(cnpj => cnpj.trim()).filter(Boolean))];
}

export function RelatoriosProvider({ children }: PropsWithChildren) {
  const { filtros: filtrosHeader } = useHeader();
  const [filtros, setFiltrosState] = useState<FiltrosRelatorio>(FILTROS_RELATORIO_INICIAIS);

  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(10);
  const [filtrosHeaderAplicados, setFiltrosHeaderAplicados] = useState<FiltrosHeaderSnapshot>({
    empresaId: filtrosHeader.empresaId,
    empresaCnpjs: filtrosHeader.empresaCnpjs,
    dataInicio: filtrosHeader.dataInicio,
    dataFim: filtrosHeader.dataFim,
    periodoModo: filtrosHeader.periodoModo,
    periodoMes: filtrosHeader.periodoMes,
    periodoAno: filtrosHeader.periodoAno,
  });

  const setFiltros = (novosFiltros: Partial<FiltrosRelatorio>) => {
    setFiltrosState(prev => ({ ...prev, ...novosFiltros }));
    setPaginaAtual(1);
  };

  const cnpjsSelecionados = useMemo(() => {
    return extrairCnpjsSelecionados(filtrosHeaderAplicados);
  }, [filtrosHeaderAplicados]);

  const cnpjsSelecionadosNoHeader = useMemo(() => {
    return extrairCnpjsSelecionados({
      empresaId: filtrosHeader.empresaId,
      empresaCnpjs: filtrosHeader.empresaCnpjs,
    });
  }, [filtrosHeader.empresaCnpjs, filtrosHeader.empresaId]);

  const empresaSelecionadaNoHeader = useMemo(() => {
    const empresaId = filtrosHeader.empresaId?.trim();
    return empresaId ? empresaId : null;
  }, [filtrosHeader.empresaId]);

  const hasEmpresaSelecionada = cnpjsSelecionados.length > 0;
  const hasEmpresaSelecionadaNoHeader = Boolean(empresaSelecionadaNoHeader);

  const entradaSaida = useMemo(() => {
    if (filtros.operacao === 'Todos') return '';
    return filtros.operacao === 'Entrada' ? '0' : '1';
  }, [filtros.operacao]);

  const tipoDocumento = filtros.documento === 'Todos' ? '' : filtros.documento;

  const { data: relatorios, isLoading } = useListarRelatorios({
    tipoRelatorio: filtros.tipoRelatorio,
    entradaSaida,
    tipoDocumento,
    cnpjContribuinte: filtrosHeaderAplicados.empresaId ?? '',
  });

  const handleBuscar = useCallback(() => {
    setFiltrosHeaderAplicados({
      empresaId: filtrosHeader.empresaId,
      empresaCnpjs: filtrosHeader.empresaCnpjs,
      dataInicio: filtrosHeader.dataInicio,
      dataFim: filtrosHeader.dataFim,
      periodoModo: filtrosHeader.periodoModo,
      periodoMes: filtrosHeader.periodoMes,
      periodoAno: filtrosHeader.periodoAno,
    });
  }, [filtrosHeader]);

  /*
    Decisão 11 da SPEC-017: depois de um "Buscar" no cabeçalho, esta tela abre já buscada
    com o mesmo recorte. `filtros.aplicado` diz que houve aquele clique, e ele se desfaz
    quando a pessoa mexe no recorte — então isto não dispara enquanto ela escolhe.

    A guarda contra laço é o PRÓPRIO instantâneo: enquanto ele estiver defasado do
    cabeçalho, há o que aplicar; aplicar o iguala, e o efeito para. Este provider não tem
    `buscaAplicada` como os outros três, e inventar um estado só para isso seria guardar
    duas vezes a mesma informação.
  */
  const recorteDefasado =
    filtrosHeaderAplicados.empresaId !== filtrosHeader.empresaId ||
    filtrosHeaderAplicados.empresaCnpjs !== filtrosHeader.empresaCnpjs ||
    filtrosHeaderAplicados.dataInicio !== filtrosHeader.dataInicio ||
    filtrosHeaderAplicados.dataFim !== filtrosHeader.dataFim ||
    filtrosHeaderAplicados.periodoModo !== filtrosHeader.periodoModo ||
    filtrosHeaderAplicados.periodoMes !== filtrosHeader.periodoMes ||
    filtrosHeaderAplicados.periodoAno !== filtrosHeader.periodoAno;

  useEffect(() => {
    if (!filtrosHeader.aplicado) return;
    if (!recorteDefasado) return;
    handleBuscar();
  }, [filtrosHeader.aplicado, recorteDefasado, handleBuscar]);

  const handleLimparFiltrosGlobais = useCallback(() => {
    setFiltrosState(FILTROS_RELATORIO_INICIAIS);
    setPaginaAtual(1);
    setItensPorPagina(10);
    setFiltrosHeaderAplicados(criarFiltrosHeaderPadrao());
  }, []);

  const relatoriosFiltrados = useMemo<Relatorio[]>(() => {
    const relatoriosTransformados =
      relatorios?.map(relatorio => ({
        id: relatorio.ID.toString(),
        numero: relatorio.Numeracao_Relatorio,
        nome: relatorio.Nome_Relatorio,
        tipo: relatorio.Tipo,
        entradaSaida: relatorio.EntradaSaida,
        documento: relatorio.Doc,
        descricao: relatorio.Descricao_Regra,
        status: relatorio.Status,
        itens: [],
      })) ?? [];

    if (!filtros.busca) {
      return relatoriosTransformados;
    }

    const busca = filtros.busca.toLowerCase();
    return relatoriosTransformados.filter(
      relatorio => relatorio.nome.toLowerCase().includes(busca) || relatorio.numero.includes(busca)
    );
  }, [filtros.busca, relatorios]);

  const value = useMemo(
    (): RelatoriosContextValue => ({
      filtros,
      setFiltros,
      relatorios: relatoriosFiltrados,
      paginaAtual,
      setPaginaAtual,
      itensPorPagina,
      setItensPorPagina,
      filtrosHeaderAplicados,
      cnpjsSelecionados,
      empresaSelecionadaNoHeader,
      cnpjsSelecionadosNoHeader,
      hasEmpresaSelecionada,
      hasEmpresaSelecionadaNoHeader,
      handleBuscar,
      handleLimparFiltrosGlobais,
      isLoading,
    }),
    [
      filtros,
      relatoriosFiltrados,
      isLoading,
      paginaAtual,
      itensPorPagina,
      filtrosHeaderAplicados,
      cnpjsSelecionados,
      empresaSelecionadaNoHeader,
      cnpjsSelecionadosNoHeader,
      hasEmpresaSelecionada,
      hasEmpresaSelecionadaNoHeader,
      handleBuscar,
      handleLimparFiltrosGlobais,
    ]
  );

  return <RelatoriosContext.Provider value={value}>{children}</RelatoriosContext.Provider>;
}
